import { Route } from '@angular/router';
import { AuthGuard } from '@freesixtyfive/auth';
import {
  provideSiteBevHoldersApi,
  provideSiteSimulationMock,
} from '@fsf/discover';
import { _ } from '@ngx-translate/core';
import { provideEchartsCore } from 'ngx-echarts';
import { MenuItem } from 'primeng/api';
import { permissionGuard } from './auth/permission.guard';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { StartPageComponent } from './pages/start-page/start-page.component';

export const appRoutes: Route[] = [
  {
    path: '',
    redirectTo: 'start',
    pathMatch: 'full',
  },
  {
    path: 'start',
    title: 'Start',
    component: StartPageComponent,
    canActivate: [AuthGuard],
    data: {
      breadcrumbs: [{ label: _('label.start') }] as MenuItem[],
    },
  },
  {
    path: 'login',
    title: 'Login',
    component: LoginPageComponent,
    data: {
      breadcrumbs: [{ label: _('label.login') }] as MenuItem[],
    },
  },
  {
    path: 'forgot-password',
    title: 'Forgot Password',
    component: ForgotPasswordComponent,
    data: {
      breadcrumbs: [{ label: _('label.forgotPassword') }] as MenuItem[],
    },
  },
  {
    path: 'reset-password/:hash',
    title: 'Reset Password',
    component: ResetPasswordComponent,
    data: {
      breadcrumbs: [{ label: _('label.resetPassword') }] as MenuItem[],
    },
  },
  {
    path: 'change-password',
    title: 'Change Password',
    component: ResetPasswordComponent,
    data: {
      breadcrumbs: [{ label: _('label.changePassword') }] as MenuItem[],
    },
  },
  {
    path: 'set-password/:hash',
    title: 'Set Password',
    component: ResetPasswordComponent,
    data: {
      breadcrumbs: [{ label: _('label.setPassword') }] as MenuItem[],
    },
  },
  {
    path: 'users',
    title: 'Users',
    canActivate: [AuthGuard, permissionGuard],
    loadComponent: () =>
      import('./pages/users-page/users-page.component').then(
        (c) => c.UsersPageComponent,
      ),
    data: {
      permission: 'manage:users',
      breadcrumbs: [{ label: _('label.editUsers') }] as MenuItem[],
    },
  },
  {
    path: 'discover/sites',
    title: 'Sites',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./pages/discover/discover-sites-page/discover-sites-page.component').then(
        (c) => c.DiscoverSitesPageComponent,
      ),
    data: {
      breadcrumbs: [{ label: _('label.discoverSites') }] as MenuItem[],
    },
  },
  {
    // Bare site URL — redirect to the default (competitors) tab so the active tab
    // always lives in the URL (`:id/:tab`). `pathMatch: 'full'` keeps this from
    // swallowing the longer `:id/:tab` route below.
    path: 'discover/sites/:id',
    pathMatch: 'full',
    redirectTo: ({ params }) => `/discover/sites/${params['id']}/competitors`,
  },
  {
    // `:id` and `:tab` live on the same route so a single ActivatedRoute carries
    // both params (a non-empty child route would not inherit `:id` under the
    // default 'emptyOnly' param-inheritance strategy).
    path: 'discover/sites/:id/:tab',
    title: 'Site detail',
    canActivate: [AuthGuard],
    providers: [
      // Site insights are served by the root SiteInsightsStore (backed by the real
      // `/candidate-sites/{id}/insights` endpoint, wired at the app root), so no route-scoped
      // provider is needed here — the mock repository/seam was retired.
      provideSiteSimulationMock(),
      // Real Laravel-backed BEV-holders lookup (swapped from the mock). The seam is
      // unchanged, so the consuming simulation component is not touched. Requires the
      // backend endpoint to be migrated + seeded and live before this ships (deploy order).
      provideSiteBevHoldersApi(),
      // Lazy echarts: the module (core + tree-shaken parts) is fetched only when the
      // chart directive first initializes, so it stays out of the eager main bundle.
      provideEchartsCore({
        echarts: () =>
          import('./pages/discover/echarts-setup').then((m) => m.default),
      }),
    ],
    loadComponent: () =>
      import('./pages/discover/discover-site-detail-page/discover-site-detail-page.component').then(
        (c) => c.DiscoverSiteDetailPageComponent,
      ),
    data: {
      breadcrumbs: [
        { label: _('label.discoverSites'), routerLink: '/discover/sites' },
        { label: _('label.discoverSiteDetail') },
      ] as MenuItem[],
    },
  },
  {
    path: 'discover',
    title: 'Site discovery',
    canActivate: [AuthGuard, permissionGuard],
    loadComponent: () =>
      import('./pages/discover/discover-map-page/discover-map-page.component').then(
        (c) => c.DiscoverMapPageComponent,
      ),
    data: {
      permission: 'manage:sites',
      breadcrumbs: [{ label: _('label.discoverMap') }] as MenuItem[],
    },
  },
  {
    path: '**',
    redirectTo: 'start',
  },
];
